import { ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  FAR_BADGE_PERCENT,
  FAR_FILE_INVOICE,
  FAR_GEAR,
  FAR_GIFT,
  FAR_LIST_ALT,
  FAS_BADGE_PERCENT,
  FAS_FILE_INVOICE,
  FAS_GEAR,
  FAS_GIFT,
  FAS_LIST_ALT
} from 'const/icons'
import routes from 'const/routes'
import i18n from 'strings/i18n'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import {
  BrandRefreshMenuButton,
  IconContainer,
  MenuButton,
  MenuContainer
} from './subcomponents'

interface MobileMenuNavigationProps {
  isStatementsButtonEnabled: boolean
  isPromotionsButtonEnabled: boolean
  showAccountServiceMenu: boolean
  isRewardsButtonEnabled: boolean
}

interface NavMenuItemProps {
  isActive: boolean
  activeIcon: IconProp
  inactiveIcon: IconProp
  label: ReactElement | string
  route: string
  dataType: string
  dataReason: string
  dataTest?: string
  dataCyValue?: string
}

const getActiveStates = (pathname: string) => ({
  isActivityActive:
    pathname === routes.ACTIVITY || pathname === routes.DISPUTES,
  isStatementsActive: pathname === routes.STATEMENTS,
  isPromotionsActive: pathname === routes.PROMOTIONS,
  isAccountServicesActive: pathname === routes.ACCOUNT_SERVICES,
  isRewardsActive: pathname === routes.REWARDS
})

const NavMenuItem = ({
  isActive,
  activeIcon,
  inactiveIcon,
  label,
  route,
  dataType,
  dataReason,
  dataTest,
  dataCyValue
}: NavMenuItemProps): ReactElement => {
  const navigate = useNavigate()
  const brandRefreshEnabled = isBrandRefreshEnabled()
  const NavButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshMenuButton
    : MenuButton
  const variantProps = brandRefreshEnabled
    ? { variant: 'tertiary' as const }
    : { buttonType: 'text' as const }
  const getCyProp = () => {
    if (!dataCyValue) return {}
    return brandRefreshEnabled
      ? { 'data-cy': dataCyValue }
      : { dataCy: dataCyValue }
  }

  return (
    <NavButton
      $isActive={isActive}
      aria-current={isActive ? 'page' : undefined}
      {...variantProps}
      data-type={dataType}
      data-reason={dataReason}
      data-object="button"
      data-analytics="clicktrackingevent"
      {...(dataTest && { 'data-test': dataTest })}
      {...getCyProp()}
      onClick={() => {
        navigate(route)
      }}
    >
      <IconContainer icon={isActive ? activeIcon : inactiveIcon} />
      {label}
    </NavButton>
  )
}

const MobileMenuNavigation = ({
  isStatementsButtonEnabled,
  isPromotionsButtonEnabled,
  showAccountServiceMenu,
  isRewardsButtonEnabled
}: MobileMenuNavigationProps): ReactElement => {
  const location = useLocation()
  const {
    isActivityActive,
    isStatementsActive,
    isPromotionsActive,
    isAccountServicesActive,
    isRewardsActive
  } = getActiveStates(location.pathname)

  return (
    <MenuContainer aria-label="Account navigation">
      <NavMenuItem
        isActive={isActivityActive}
        activeIcon={FAS_LIST_ALT}
        inactiveIcon={FAR_LIST_ALT}
        label={i18n({ labels: 'activityMenu' })}
        route={routes.ACTIVITY}
        dataType="activity"
        dataReason="activity"
        dataCyValue="activity-button"
      />
      {isStatementsButtonEnabled && (
        <NavMenuItem
          isActive={isStatementsActive}
          activeIcon={FAS_FILE_INVOICE}
          inactiveIcon={FAR_FILE_INVOICE}
          label={i18n({ labels: 'statements' })}
          route={routes.STATEMENTS}
          dataType="statements"
          dataReason="statements"
          dataTest="statements-nav-link"
          dataCyValue="statements-nav-link"
        />
      )}
      {isPromotionsButtonEnabled && (
        <NavMenuItem
          isActive={isPromotionsActive}
          activeIcon={FAS_BADGE_PERCENT}
          inactiveIcon={FAR_BADGE_PERCENT}
          label={i18n({ labels: 'promotionsMenu' })}
          route={routes.PROMOTIONS}
          dataType="promotions"
          dataReason="header-nav"
          dataTest="promotions-nav-link"
        />
      )}
      {showAccountServiceMenu && (
        <NavMenuItem
          isActive={isAccountServicesActive}
          activeIcon={FAS_GEAR}
          inactiveIcon={FAR_GEAR}
          label={i18n({ labels: 'accountsServices' })}
          route={routes.ACCOUNT_SERVICES}
          dataType="account services"
          dataReason="account services"
          dataTest="account-services"
          dataCyValue="account-services-button"
        />
      )}
      {isRewardsButtonEnabled && (
        <NavMenuItem
          isActive={isRewardsActive}
          activeIcon={FAS_GIFT}
          inactiveIcon={FAR_GIFT}
          label={i18n({ button: 'rewards' })}
          route={routes.REWARDS}
          dataType="rewards"
          dataReason="rewards"
          dataTest="rewards"
        />
      )}
    </MenuContainer>
  )
}

export default MobileMenuNavigation
